/**
 * Curious Baby cart upsell behaviour.
 *
 * The drawer's visible state is rendered by Liquid, so it survives Broadcast's
 * section re-render. This file only handles the things that change the cart,
 * then hands control back to Broadcast:
 *
 *   theme:cart:refresh      re-render the drawer
 *   theme:cart-drawer:open  open it
 *   theme:product:added     fired by Broadcast after an add to cart
 *
 * Everything is delegated off document, so it keeps working after a re-render.
 */
(function () {
  'use strict';

  var CFG = window.CB_CART || {};
  var busy = false;
  var busySince = 0;

  /**
   * Whether the picker has already been offered for this qualifying cart.
   * Kept in sessionStorage rather than a variable: a plain variable resets on
   * every page load, so the picker reappeared on each navigation.
   */
  var OFFER_KEY = 'cbGiftOffered';
  function alreadyOffered() {
    try {
      return sessionStorage.getItem(OFFER_KEY) === '1';
    } catch (e) {
      return false;
    }
  }
  function setOffered(v) {
    try {
      if (v) sessionStorage.setItem(OFFER_KEY, '1');
      else sessionStorage.removeItem(OFFER_KEY);
    } catch (e) {
      /* private browsing, fall through */
    }
  }

  /**
   * Shipping protection is a real line item, so "pre-selected" means adding it
   * on the shopper's behalf the first time the bag has something in it. Opting
   * out has to stick, or it would be re-added on the next refresh and the
   * shopper could never actually decline it.
   */
  var OPTOUT_KEY = 'cbProtectOptOut';
  function protectionDeclined() {
    try {
      return sessionStorage.getItem(OPTOUT_KEY) === '1';
    } catch (e) {
      return false;
    }
  }
  function setProtectionDeclined(v) {
    try {
      if (v) sessionStorage.setItem(OPTOUT_KEY, '1');
      else sessionStorage.removeItem(OPTOUT_KEY);
    } catch (e) {
      /* private browsing */
    }
  }

  function syncProtection() {
    if (busy) return;

    var root = document.querySelector('[data-cb-root]');
    if (!root) return;
    if (root.getAttribute('data-cb-protect-preselect') !== 'true') return;
    if (root.classList.contains('is-empty')) return;
    // Nothing ships, nothing to protect. Without this a bag of digital guides or
    // a membership on its own had protection silently added to it.
    if (root.getAttribute('data-cb-has-physical') !== 'true') return;
    if (root.getAttribute('data-cb-protection-on') === 'true') return;
    if (protectionDeclined()) return;

    var variant = root.getAttribute('data-cb-protect-variant');
    if (!variant) return;

    guard(addVariant(variant));
  }

  /**
   * Membership box dismissal. A shopper who says no thanks should not be asked
   * again on the next cart change, so the answer is remembered for the session.
   */
  var CLUB_KEY = 'cbClubDismissed';
  function clubDismissed() {
    try {
      return sessionStorage.getItem(CLUB_KEY) === '1';
    } catch (e) {
      return false;
    }
  }
  /**
   * Dismissed hides the whole block (step label included) and leaves a one-line
   * teaser in its place, so the offer is recoverable without nagging.
   */
  // behavior:'smooth' is silently swallowed on the drawer body: the body-scroll-lock
// that keeps the page behind the drawer still cancels native smooth scrolling, so
// scrollTo({behavior:'smooth'}) leaves scrollTop untouched while 'auto' and a
// direct assignment both work. Tween it by hand instead.
function smoothScrollTo(el, target) {
  var reduce = window.matchMedia &&
               window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // requestAnimationFrame is frozen while the document is hidden, so a tween
  // started there would never run and the drawer would simply not move. Land
  // on the target immediately instead: nobody is watching the animation.
  var hidden = document.visibilityState && document.visibilityState !== 'visible';
  var start = el.scrollTop;
  var dist = target - start;
  if (reduce || hidden || Math.abs(dist) < 2) {
    el.scrollTop = target;
    return;
  }
  var DURATION = 420;
  var t0 = null;
  var done = false;
  function frame(now) {
    if (t0 === null) t0 = now;
    var p = (now - t0) / DURATION;
    if (p > 1) p = 1;
    // easeOutCubic
    el.scrollTop = start + dist * (1 - Math.pow(1 - p, 3));
    if (p < 1) { window.requestAnimationFrame(frame); } else { done = true; }
  }
  window.requestAnimationFrame(frame);
  // Safety net for a tab that is backgrounded mid-tween, which starves rAF and
  // would otherwise strand the scroll part-way.
  window.setTimeout(function () { if (!done) el.scrollTop = target; }, DURATION + 200);
}

// Choosing a gift must REPLACE the one already in the bag, never stack on top of
// it. This reads the cart live at click time instead of trusting a line key
// rendered into the markup: that key only refreshes when the drawer re-renders,
// so picking a second gift before the re-render landed found nothing to remove
// and simply added another. Clearing by VARIANT in one update.js call also
// repairs a bag that has already stacked several up.
function swapGift(newVariantId) {
  var ids = (window.CB_CART || {}).giftProductIds || [];
  return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
    .then(function (r) { return r.json(); })
    .then(function (cart) {
      var updates = {};
      var found = 0;
      for (var i = 0; i < cart.items.length; i++) {
        var it = cart.items[i];
        if (ids.indexOf(it.product_id) !== -1) {
          updates[it.variant_id] = 0;
          found++;
        }
      }
      if (!found) return null;
      return post('/cart/update.js', { updates: updates });
    })
    .then(function () {
      return addVariant(newVariantId);
    });
}

// Protection has to LEAVE a bag that stops being shippable, not just stop being
// added to it: remove the last physical item and the protection line would
// otherwise sit there charging for nothing.
var droppingProtection = false;
function dropProtectionIfDigital() {
  if (droppingProtection) return Promise.resolve();
  var root = document.querySelector('[data-cb-root]');
  if (!root || root.getAttribute('data-cb-has-physical') === 'true') return Promise.resolve();
  var vids = (window.CB_CART || {}).protectionVariantIds || [];
  if (!vids.length) return Promise.resolve();
  return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
    .then(function (r) { return r.json(); })
    .then(function (cart) {
      var updates = {};
      var found = 0;
      for (var i = 0; i < cart.items.length; i++) {
        if (vids.indexOf(cart.items[i].variant_id) !== -1) {
          updates[cart.items[i].variant_id] = 0;
          found++;
        }
      }
      if (!found) return null;
      droppingProtection = true;
      return post('/cart/update.js', { updates: updates })
        .then(function (res) { droppingProtection = false; refresh(); return res; })
        .catch(function (e) { droppingProtection = false; throw e; });
    })
    .catch(function () { droppingProtection = false; });
}

// Protection is a footer toggle, not a shopping-bag line. Broadcast renders the
// line list itself, and the hide-line setting only ever corrected the item
// COUNT, so the row still appeared in the bag. Hide the row itself.
function hideProtectionLine() {
  var cfg = window.CB_CART || {};
  if (!cfg.protectionHideLine) return;
  var vids = cfg.protectionVariantIds || [];
  if (!vids.length) return;
  var rows = document.querySelectorAll('[data-items-holder] .cart__item, .cb-items .cart__item');
  for (var i = 0; i < rows.length; i++) {
    var rm = rows[i].querySelector('[data-item-remove]');
    var key = rm ? rm.getAttribute('data-id') || '' : '';
    var vid = parseInt(String(key).split(':')[0], 10);
    if (vid && vids.indexOf(vid) !== -1) rows[i].classList.add('cb-is-protection');
  }
}

// ONE GIFT, QUANTITY ONE. Shopify has no native per-variant order limit outside
// B2B quantity rules, and a $0.00 gift variant is addable by anyone who knows the
// id, so the rule is enforced here and re-enforced on every cart change rather
// than assumed. Two ways it can drift: a second gift gets added alongside the
// first, or the shopper increments the quantity stepper on the gift line. This
// repairs both, and only fires when something is actually wrong, so it cannot
// loop against its own refresh.
var fixingGift = false;
function enforceOneGift() {
  if (fixingGift) return Promise.resolve();
  var ids = (window.CB_CART || {}).giftProductIds || [];
  if (!ids.length) return Promise.resolve();
  return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
    .then(function (r) { return r.json(); })
    .then(function (cart) {
      var gifts = [];
      for (var i = 0; i < cart.items.length; i++) {
        if (ids.indexOf(cart.items[i].product_id) !== -1) gifts.push(cart.items[i]);
      }
      if (!gifts.length) return null;
      var totalQty = 0;
      for (var j = 0; j < gifts.length; j++) totalQty += gifts[j].quantity;
      if (gifts.length === 1 && totalQty === 1) return null; // already correct

      // Keep the LAST one: if two are present the later pick is what they chose.
      var keep = gifts[gifts.length - 1];
      var updates = {};
      for (var k = 0; k < gifts.length; k++) updates[gifts[k].variant_id] = 0;
      updates[keep.variant_id] = 1;
      fixingGift = true;
      return post('/cart/update.js', { updates: updates })
        .then(function (res) { fixingGift = false; refresh(); return res; })
        .catch(function (e) { fixingGift = false; throw e; });
    })
    .catch(function () { fixingGift = false; });
}

// The gift line must not offer a quantity stepper at all: nudging it to 2 is a
// bug the shopper should never be invited to create. Broadcast's stepper markup
// is found by its quantity input rather than by class name, which is brittle.
function lockGiftLines() {
  var vids = (window.CB_CART || {}).giftVariantIds || [];
  if (!vids.length) return;
  var rows = document.querySelectorAll('[data-items-holder] .cart__item, .cb-items .cart__item');
  for (var i = 0; i < rows.length; i++) {
    var row = rows[i];
    var rm = row.querySelector('[data-item-remove]');
    var key = rm ? rm.getAttribute('data-id') || '' : '';
    var vid = parseInt(String(key).split(':')[0], 10);
    if (!vid || vids.indexOf(vid) === -1) continue;
    row.classList.add('cb-is-gift');
    var qty = row.querySelector('input[name="updates[]"], input[type="number"], [data-quantity-input]');
    if (!qty) continue;

    // Hide the stepper WITHOUT hiding Remove. Broadcast nests the remove link in
    // the same block as the quantity controls, so hiding the obvious wrapper
    // took Remove with it and stranded the gift in the bag. A shopper who wants
    // no gift at all must always be able to take it out.
    var wrap = qty.closest('quantity-input') || qty.parentElement;
    if (wrap && !wrap.querySelector('[data-item-remove]')) {
      wrap.classList.add('cb-qty-locked');
    } else {
      // Remove shares this container: hide only the controls themselves. The
      // remove link is an <a>, the steppers are <button>s, so buttons are safe.
      qty.classList.add('cb-qty-locked');
      var ctrls = (wrap || row).querySelectorAll('button');
      for (var c = 0; c < ctrls.length; c++) {
        if (!ctrls[c].closest('[data-item-remove]')) ctrls[c].classList.add('cb-qty-locked');
      }
    }
  }
}

// The credit strip lives in the pinned header; the Club box it points at is far
// down the scrolling body. If the shopper has dismissed the Club, restore it
// first, otherwise the jump would scroll to something that is not there.
function jumpToClub() {
  if (clubDismissed()) {
    setClubDismissed(false);
    applyClubDismissal();
  }
  var block = document.querySelector('[data-cb-club-block]');
  if (!block) return;
  var box = block.querySelector('.cb-club') || block;

  // scrollIntoView picks the wrong ancestor here: the panel is transformed and
  // .cb-inner is overflow:hidden, so the browser scrolls something other than
  // the drawer body and nothing appears to happen. Drive the scroller directly
  // off measured rects instead, which is exact regardless of offsetParent.
  var scroller = block.closest('[data-cart-drawer-body]') ||
                 document.querySelector('.cb-body');
  if (scroller) {
    var delta = block.getBoundingClientRect().top -
                scroller.getBoundingClientRect().top;
    var target = scroller.scrollTop + delta - 12;
    if (target < 0) target = 0;
    smoothScrollTo(scroller, target);
  }
  box.classList.remove('is-landed');
  // reflow, so the animation restarts if they tap the strip twice
  void box.offsetWidth;
  box.classList.add('is-landed');
  var focusable = block.querySelector('button, [href], input');
  if (focusable && typeof focusable.focus === 'function') {
    focusable.focus({ preventScroll: true });
  }
}

function applyClubDismissal() {
    var block = document.querySelector('[data-cb-club-block]');
    var teaser = document.querySelector('[data-cb-restore-club]');
    var off = clubDismissed();
    if (block) block.hidden = off;
    if (teaser) teaser.hidden = !off;
  }

  function setClubDismissed(v) {
    try {
      if (v) sessionStorage.setItem(CLUB_KEY, '1');
      else sessionStorage.removeItem(CLUB_KEY);
    } catch (e) {}
    applyClubDismissal();
  }

  /**
   * The round trip to Shopify and back takes a couple of seconds. Without this
   * the switch sits still the whole time and reads as broken, so answer the tap
   * straight away and lock it until the cart catches up.
   */
  function flipSwitch(el, on) {
    var sw = el.classList && el.classList.contains('cb-switch') ? el : document.querySelector('.cb-switch');
    if (!sw) return;
    sw.setAttribute('aria-checked', on ? 'true' : 'false');
    sw.setAttribute('aria-busy', 'true');
  }

  function drawerIsOpen() {
    var d = document.getElementById('cart-drawer');
    return !!d && d.classList.contains('is-open');
  }

  /* ------------------------------------------------------------------ *
   * cart api
   * ------------------------------------------------------------------ */

  function refresh() {
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
  }

  function openDrawer() {
    // :open is in Broadcast's event list but does not actually open the panel.
    // :show is the one that does. Both are dispatched so behaviour is the same
    // whichever a future Broadcast build listens to.
    document.dispatchEvent(new CustomEvent('theme:cart-drawer:show', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('theme:cart-drawer:open', { bubbles: true }));
  }

  var errorTimer = null;
  function showError(message) {
    // Broadcast owns the error surface, so reuse it rather than alerting. But
    // its box computes to visibility:hidden/opacity:0/height:0 and adding
    // is-active alone does not reveal it, so every failure wrote text into an
    // invisible element and read to the shopper as the button doing nothing.
    // cb-error-open is ours and is styled to actually show.
    var box = document.querySelector('[data-cart-errors]');
    var msg = document.querySelector('[data-error-message]');
    if (!box || !msg) return;
    msg.textContent = message;
    box.classList.add('is-active', 'cb-error-open');
    if (errorTimer) window.clearTimeout(errorTimer);
    errorTimer = window.setTimeout(hideError, 6000);
  }
  function hideError() {
    var box = document.querySelector('[data-cart-errors]');
    if (box) box.classList.remove('is-active', 'cb-error-open');
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) throw new Error(data.description || data.message || 'Cart update failed');
        return data;
      });
    });
  }

  function addVariant(id, sellingPlan) {
    var item = { id: Number(id), quantity: 1 };
    if (sellingPlan) item.selling_plan = Number(sellingPlan);
    return post('/cart/add.js', { items: [item] });
  }

  function removeKey(key, variantId) {
    return post('/cart/change.js', { id: key, quantity: 0 }).catch(function (err) {
      // Shopify can refuse a change.js call on a line it considers unavailable,
      // including a subscription line whose variant is oversold, and it refuses
      // even when the request only asks to REMOVE it. update.js keys by variant
      // and gets through where change.js does not, so try that before giving up.
      if (!variantId) throw err;
      var updates = {};
      updates[variantId] = 0;
      return post('/cart/update.js', { updates: updates });
    });
  }

  // The membership's line key is baked into the button when the section
  // renders, but Shopify line keys change as the cart changes and this box does
  // not re-render on every change. A stale key makes "Remove membership" fail,
  // and it fails quietly, which reads as the button being dead. Look the line up
  // live at click time and keep the rendered key only as a fallback.
  function removeClubLine(fallbackKey) {
    var clubId = (window.CB_CART || {}).clubProductId;
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var key = '';
        for (var i = 0; i < cart.items.length; i++) {
          if (clubId && cart.items[i].product_id === clubId) {
            key = cart.items[i].key;
            break;
          }
        }
        var variantId = 0;
        for (var j = 0; j < cart.items.length; j++) {
          if (clubId && cart.items[j].product_id === clubId) {
            variantId = cart.items[j].variant_id;
            break;
          }
        }
        if (!key) key = fallbackKey;
        if (!key) throw new Error('Could not find the membership in your bag.');
        return removeKey(key, variantId);
      });
  }

  function guard(promise) {
    // A stuck busy flag silently swallows every later click, and a swallowed
    // click is indistinguishable from a dead button. If anything ever leaves
    // this set, let it expire rather than bricking the drawer for the session.
    if (busy && Date.now() - busySince < 8000) return Promise.resolve();
    busy = true;
    busySince = Date.now();
    return promise
      .then(function (r) { hideError(); return r; })
      .then(refresh)
      .catch(function (err) {
        showError(err.message);
      })
      .then(function () {
        busy = false;
      });
  }

  /* ------------------------------------------------------------------ *
   * keeping our own regions fresh
   *
   * Broadcast refreshes the cart by fetching ?section_id=api-cart-items and
   * swapping only [data-items-holder] and [data-cart-price-holder]. Everything
   * this file adds sits outside those, so it would otherwise keep showing the
   * totals from before the change. We re-fetch the drawer section and swap our
   * own regions, and never touch Broadcast's.
   * ------------------------------------------------------------------ */

  var SWAP = ['[data-cb-ladder]', '[data-cb-extras]', '[data-cb-foot]', '[data-cb-rail-list]'];
  var STATE_ATTRS = [
    'data-cb-merch',
    'data-cb-ship-tier',
    'data-cb-gift-tier',
    'data-cb-gift-chosen',
    'data-cb-current-gift-key',
    'data-cb-protection-on'
  ];
  var syncing = false;

  /**
   * Broadcast's <cart-drawer> binds its close button ONCE, in its constructor,
   * and its <cart-items> controller only exists when the bag has something in
   * it. So on a page that loads with an EMPTY cart, the first add to cart:
   *   1. has no <cart-items> to render it, so the drawer never opens and is
   *      left with is-loading, and
   *   2. makes syncDrawer rebuild the whole panel, which swaps in a new close
   *      button nobody is listening to (the "X does nothing" bug, 2026-09-25).
   * After a structural rebuild, point Broadcast at the new button, clear the
   * stuck loading state, and open the drawer when the bag just became filled.
   */
  function rewireDrawer(becameFilled) {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    var closer = drawer.querySelector('[data-cart-drawer-close]');
    if (closer && drawer.cartDrawerClose !== closer) {
      drawer.cartDrawerClose = closer;
      closer.addEventListener('click', function (e) {
        e.preventDefault();
        if (typeof drawer.closeCartDrawer === 'function') drawer.closeCartDrawer();
      });
    }
    var inner = drawer.querySelector('[data-cart-drawer-inner]');
    if (inner) drawer.cartDrawerInner = inner;
    drawer.classList.remove('is-loading');
    if (becameFilled && !drawer.classList.contains('is-open')) {
      // :show must be dispatched ON the element; document-level does nothing
      drawer.dispatchEvent(new CustomEvent('theme:cart-drawer:show'));
    }
  }

  function syncDrawer() {
    if (syncing) return Promise.resolve();
    syncing = true;

    return fetch(window.location.pathname + '?section_id=cart-drawer')
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-cb-root]');
        var live = document.querySelector('[data-cb-root]');
        if (!fresh || !live) return;

        STATE_ATTRS.forEach(function (a) {
          var v = fresh.getAttribute(a);
          if (v !== null) live.setAttribute(a, v);
          else live.removeAttribute(a);
        });

        // is-empty drives whether the gift picker may show at all
        live.classList.toggle('is-empty', fresh.classList.contains('is-empty'));

        /**
         * An empty bag renders no ladder, no steps, no line items and no
         * footer at all, so going empty -> filled (or back) changes the panel's
         * structure rather than just its contents. Targeted swaps have nothing
         * to write into in that case, which left the drawer blank after the
         * first add of a session. Rebuild the whole panel when the structure
         * differs, and only do the cheap targeted swaps when it does not.
         */
        var liveFilled = !!document.querySelector('[data-cb-ladder]');
        var freshFilled = !!doc.querySelector('[data-cb-ladder]');

        if (liveFilled !== freshFilled) {
          var liveInner = document.querySelector('.cb-inner');
          var freshInner = doc.querySelector('.cb-inner');
          if (liveInner && freshInner) liveInner.innerHTML = freshInner.innerHTML;
          // The rebuild replaced the close button Broadcast wired up once, in
          // its constructor. Reconnect it, and finish the first add of a
          // session (see rewireDrawer).
          rewireDrawer(freshFilled);
        } else {
          SWAP.forEach(function (sel) {
            var a = document.querySelector(sel);
            var b = doc.querySelector(sel);
            if (a && b) a.innerHTML = b.innerHTML;
            else if (a && !b) a.innerHTML = '';
          });
        }

        // Broadcast rewrites the bag badge straight from the cart API, which
        // counts the protection line the shopper never chose. Our render already
        // excludes it, so take the count from there.
        var freshCount = doc.querySelector('[data-cart-count]');
        if (freshCount) {
          var n = freshCount.getAttribute('data-cart-count');
          document.querySelectorAll('[data-cart-count]').forEach(function (el) {
            el.setAttribute('data-cart-count', n);
            if (!el.children.length) el.textContent = n;
          });
        }

        // The whole sidebar is conditional on the bag having something in it,
        // so hide it when it is gone rather than leaving a stale heading like
        // "Add a third deck" sitting beside an empty cart.
        var liveRail = document.querySelector('.cb-rail');
        var freshRail = doc.querySelector('.cb-rail');
        if (liveRail) {
          liveRail.hidden = !freshRail;
          var liveHead = liveRail.querySelector('.cb-rail__head');
          var freshHead = freshRail && freshRail.querySelector('.cb-rail__head');
          if (liveHead && freshHead) liveHead.innerHTML = freshHead.innerHTML;
        }

        // the gift picker itself is conditional on qualifying, so replace it too
        var liveModal = document.querySelector('[data-cb-gift-modal]');
        var freshModal = doc.querySelector('[data-cb-gift-modal]');
        if (liveModal && !freshModal) liveModal.remove();
        else if (liveModal && freshModal) {
          var wasOpen = !liveModal.hidden;
          liveModal.innerHTML = freshModal.innerHTML;
          liveModal.hidden = !wasOpen;
        } else if (!liveModal && freshModal) {
          // the picker belongs inside the drawer panel now
          var host = document.querySelector('.cb-inner') || document.querySelector('[data-section-id]');
          if (host) host.appendChild(freshModal.cloneNode(true));
        }
      })
      .catch(function () {
        /* a stale panel is bad, a broken one is worse: fail quietly */
      })
      .then(function () {
        syncing = false;
        syncGiftPicker();
        syncProtection();
        applyClubDismissal();
      });
  }

  /* ------------------------------------------------------------------ *
   * gift picker
   * ------------------------------------------------------------------ */

  function giftModal() {
    return document.querySelector('[data-cb-gift-modal]');
  }

  function openGifts() {
    var m = giftModal();
    if (!m) return;
    m.hidden = false;
    // Move focus so the picker is reachable by keyboard.
    var first = m.querySelector('.cb-gift');
    if (first) first.focus();
    document.addEventListener('keydown', onEsc);
  }

  function closeGifts() {
    var m = giftModal();
    if (!m) return;
    m.hidden = true;
    document.removeEventListener('keydown', onEsc);
  }

  function onEsc(e) {
    if (e.key !== 'Escape') return;
    closeGifts();
    closeOffer();
  }

  /**
   * Keeps the picker in sync with the cart in BOTH directions.
   *
   * Opening it when the threshold is crossed is only half the job: if the
   * shopper then removes an item, or empties the bag, an already-open picker
   * has to be dismissed or it keeps offering a gift they no longer qualify for.
   */
  function syncGiftPicker() {
    var root = document.querySelector('[data-cb-root]');

    // No cart markup at all means an empty bag. Nothing to offer.
    if (!root) {
      setOffered(false);
      closeGifts();
      return;
    }

    var merch = Number(root.getAttribute('data-cb-merch') || 0);
    var tier = Number(root.getAttribute('data-cb-gift-tier') || 0);
    var chosen = root.getAttribute('data-cb-gift-chosen') === 'true';
    var empty = root.classList.contains('is-empty');

    // Dropped back under the threshold: dismiss it and re-arm for next time.
    if (empty || !tier || merch < tier) {
      setOffered(false);
      closeGifts();
      return;
    }

    // A gift is already in the bag, so there is nothing to prompt for.
    if (chosen) return;

    // Only ever offer inside the open drawer, and only once per qualifying
    // cart. Popping a modal over the page while the shopper is browsing, or
    // again on every page they visit, is what made it feel constant.
    if (CFG.giftPickerAuto && !alreadyOffered() && drawerIsOpen()) {
      setOffered(true);
      openGifts();
    }
  }

  /**
   * Broadcast fires its cart events before the section markup is swapped in, so
   * reading the drawer straight away sees the previous total. Wait a beat.
   */
  function syncGiftPickerSoon() {
    setTimeout(syncDrawer, 120);
  }

  /* ------------------------------------------------------------------ *
   * post add-to-cart offer
   * ------------------------------------------------------------------ */

  var offerVariantId = null;

  function offerModal() {
    return document.querySelector('[data-cb-offer-modal]');
  }

  function closeOffer() {
    var m = offerModal();
    if (!m) return;
    m.hidden = true;
    offerVariantId = null;
    document.removeEventListener('keydown', onEsc);
  }

  function parsePairs(raw) {
    var map = {};
    (raw || '').split('\n').forEach(function (line) {
      var parts = line.split('>');
      if (parts.length !== 2) return;
      var from = parts[0].trim();
      var to = parts[1].trim();
      if (from && to) map[from] = to;
    });
    return map;
  }

  function money(cents) {
    var fmt = CFG.moneyFormat || '${{amount}}';
    var value = (cents / 100).toFixed(2);
    // Handles the ${{amount}} and ${{amount_no_decimals}} cases Shopify emits.
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/, value)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, value.replace('.', ','));
  }

  function fillOffer(product, pct) {
    var m = offerModal();
    if (!m) return false;

    var variant = (product.variants || []).filter(function (v) {
      return v.available;
    })[0];
    if (!variant) return false;

    var was = variant.price;
    var now = Math.round(was * (1 - pct / 100));

    offerVariantId = variant.id;

    var set = function (sel, text) {
      var el = m.querySelector(sel);
      if (el) el.textContent = text;
    };

    set('[data-cb-offer-title]', (CFG.postAdd.title || '').replace('[percent]', pct));
    set('[data-cb-offer-lede]', (CFG.postAdd.lede || '').replace('[percent]', pct));
    set('[data-cb-offer-name]', product.title);
    set('[data-cb-offer-was]', money(was));
    set('[data-cb-offer-now]', money(now));
    set('[data-cb-offer-badge]', pct + '% off');

    var img = m.querySelector('[data-cb-offer-image]');
    if (img) {
      img.src = product.featured_image
        ? product.featured_image.replace(/(\.[a-z]+)(\?|$)/i, '_400x$1$2')
        : '';
      img.alt = product.title;
    }

    m.hidden = false;
    document.addEventListener('keydown', onEsc);
    return true;
  }

  /**
   * Broadcast fires `theme:product:add` after a successful add to cart, with
   * detail {response, button}. The response is the /cart/add.js payload, so the
   * handle of what was just added is inside it. Everything else here falls back
   * to the product page's own data-product-handle.
   */
  function onProductAdded(evt) {
    if (!CFG.postAdd || !CFG.postAdd.enabled) return;

    var pairs = parsePairs(CFG.postAdd.pairs);
    var detail = evt.detail || {};
    var handle = null;

    var res = detail.response;
    if (res) {
      if (res.items && res.items.length) handle = res.items[0].handle;
      else if (res.handle) handle = res.handle;
    }
    if (!handle && detail.product && detail.product.handle) handle = detail.product.handle;
    if (!handle && detail.variant && detail.variant.handle) handle = detail.variant.handle;
    if (!handle) {
      var meta = document.querySelector('[data-product-handle]');
      handle = meta && meta.getAttribute('data-product-handle');
    }
    if (!handle) return;

    var offerHandle = pairs[handle];
    if (!offerHandle) return;

    fetch('/products/' + offerHandle + '.js')
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (product) {
        if (!product) return;

        // Skip the offer if it is already in the bag.
        return fetch('/cart.js')
          .then(function (r) {
            return r.json();
          })
          .then(function (cart) {
            var already = cart.items.some(function (i) {
              return i.handle === offerHandle;
            });
            if (already) return;
            fillOffer(product, CFG.postAdd.discount);
          });
      })
      .catch(function () {
        /* offer is optional, never block the cart */
      });
  }

  /* ------------------------------------------------------------------ *
   * events
   * ------------------------------------------------------------------ */

  document.addEventListener('click', function (e) {
    var el;

    // add any variant: recommendations, gifting nudge, club
    if ((el = e.target.closest('[data-cb-add]'))) {
      e.preventDefault();
      guard(addVariant(el.getAttribute('data-cb-add'), el.getAttribute('data-cb-selling-plan')));
      return;
    }

    // gift picker
    if (e.target.closest('[data-cb-open-gifts]')) {
      e.preventDefault();
      openGifts();
      return;
    }
    if (e.target.closest('[data-cb-close-gifts]')) {
      e.preventDefault();
      closeGifts();
      return;
    }
    if ((el = e.target.closest('[data-cb-choose-gift]'))) {
      e.preventDefault();
      var giftVariant = el.getAttribute('data-cb-choose-gift');
      closeGifts();
      guard(swapGift(giftVariant));
      return;
    }

    // shipping protection
    if ((el = e.target.closest('[data-cb-protect-on]'))) {
      e.preventDefault();
      setProtectionDeclined(false);
      flipSwitch(el, true);
      guard(addVariant(el.getAttribute('data-cb-protect-on')));
      return;
    }
    if ((el = e.target.closest('[data-cb-protect-off]'))) {
      e.preventDefault();
      setProtectionDeclined(true);

      // The opt-out LINK continues to checkout once the line is gone. The
      // toggle on the protection row just turns it off and stays put.
      flipSwitch(el, false);

      if (el.hasAttribute('data-cb-then-checkout')) {
        el.disabled = true;
        removeKey(el.getAttribute('data-cb-protect-off'))
          .then(function () {
            window.location.href = '/checkout';
          })
          .catch(function (err) {
            el.disabled = false;
            showError(err.message);
          });
        return;
      }

      guard(removeKey(el.getAttribute('data-cb-protect-off')));
      return;
    }

    if (e.target.closest('[data-cb-dismiss-club]')) {
      e.preventDefault();
      setClubDismissed(true);
      return;
    }
    if (e.target.closest('[data-cb-restore-club]')) {
      e.preventDefault();
      setClubDismissed(false);
      var block = document.querySelector('[data-cb-club-block]');
      if (block && block.scrollIntoView) block.scrollIntoView({ block: 'nearest' });
      return;
    }

    // membership plan chooser: switch which selling plan the join button adds
    if ((el = e.target.closest('[data-cb-plan]'))) {
      e.preventDefault();
      var group = el.closest('.cb-plans');
      if (group) {
        group.querySelectorAll('.cb-plan').forEach(function (b) {
          var on = b === el;
          b.classList.toggle('is-on', on);
          b.setAttribute('aria-checked', on ? 'true' : 'false');
        });
      }
      var join = document.querySelector('[data-cb-club-join]');
      if (join) join.setAttribute('data-cb-selling-plan', el.getAttribute('data-cb-plan'));
      return;
    }

    // membership
    if ((el = e.target.closest('[data-cb-remove-club]'))) {
      e.preventDefault();
      guard(removeClubLine(el.getAttribute('data-cb-remove-club')));
      return;
    }

    // post add-to-cart offer
    if (e.target.closest('[data-cb-offer-accept]')) {
      e.preventDefault();
      var id = offerVariantId;
      closeOffer();
      if (id) guard(addVariant(id).then(openDrawer));
      return;
    }
    if (e.target.closest('[data-cb-close-offer]')) {
      e.preventDefault();
      closeOffer();
      openDrawer();
      return;
    }

    // click the scrim to dismiss either modal
    if (e.target.classList && e.target.classList.contains('cb-modal')) {
      closeGifts();
      closeOffer();
    }
  });

  /**
   * Remove-link guard.
   *
   * Broadcast renders each remove link as
   *   <a class="cart__item__remove" href="/cart/change?line=N&quantity=0" data-item-remove data-id="<line key>">
   * and intercepts it. An installed app rewrites that href to javascript:void(0),
   * adds its own class, and its handler sends the shopper to /cart, which closes
   * the drawer. That happens on the published theme too, so it is not something
   * this drawer introduced.
   *
   * When the href has been hijacked we remove the line ourselves and refresh in
   * place. If the app is ever uninstalled the href is intact again and this
   * steps aside, leaving Broadcast to handle it natively.
   */
  document.addEventListener(
    'click',
    function (e) {
      var link = e.target.closest('[data-item-remove]');
      if (!link) return;

      var href = link.getAttribute('href') || '';
      var hijacked = href.indexOf('javascript:') === 0 || href === '' || href === '#';
      if (!hijacked) return;

      var key = link.getAttribute('data-id');
      if (!key) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      var vid = parseInt(String(key).split(':')[0], 10) || 0;
      guard(removeKey(key, vid));
    },
    true
  );

  // Broadcast 8.1.1 fires theme:product:add; older builds used :added.
  document.addEventListener('theme:product:add', onProductAdded);
  document.addEventListener('theme:product:added', onProductAdded);
  // Adding from a product page fires theme:cart:add / theme:product:add and
  // nothing else, so without these two the drawer kept the totals it was
  // rendered with on page load.
  document.addEventListener('theme:cart:add', syncGiftPickerSoon);
  document.addEventListener('theme:product:add', syncGiftPickerSoon);
  document.addEventListener('theme:cart:change', syncGiftPickerSoon);
  document.addEventListener('theme:cart:update', syncGiftPickerSoon);
  document.addEventListener('theme:cart:refresh', syncGiftPickerSoon);
  document.addEventListener('theme:cart-drawer:open', syncGiftPicker);
  document.addEventListener('theme:cart-drawer:show', syncGiftPicker);
  document.addEventListener('theme:cart-drawer:close', closeGifts);

  // Close the drawer when the shopper empties it. Deliberately scoped to
  // "became empty while open": opening an already-empty cart still shows the
  // product picks, which are the whole point of that state. Without the
  // wasNonEmpty latch this would also slam the drawer shut the moment anyone
  // opened an empty cart.
  var wasNonEmpty = false;
  function closeIfEmptied() {
    var cfg = window.CB_CART || {};
    if (!cfg.closeWhenEmptied) return;
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var drawer = document.getElementById('cart-drawer');
        var open = drawer && drawer.classList.contains('is-open');
        if (cart.item_count > 0) { wasNonEmpty = true; return; }
        if (open && wasNonEmpty) {
          // Broadcast does not listen for a theme:cart-drawer:close event, it
          // only exposes the close control in the markup. Verified: dispatching
          // that event leaves the drawer open, clicking this closes it.
          var closer = document.querySelector('[data-cart-drawer-close]');
          if (closer) closer.click();
        }
        wasNonEmpty = false;
      })
      .catch(function () {});
  }
  document.addEventListener('theme:cart:change', dropProtectionIfDigital);
  document.addEventListener('theme:cart:refresh', dropProtectionIfDigital);
  document.addEventListener('theme:cart:change', hideProtectionLine);
  document.addEventListener('theme:cart:refresh', hideProtectionLine);
  document.addEventListener('shopify:section:load', hideProtectionLine);

  document.addEventListener('theme:cart:change', enforceOneGift);
  document.addEventListener('theme:cart:update', enforceOneGift);
  document.addEventListener('theme:cart:refresh', enforceOneGift);
  document.addEventListener('theme:cart:change', lockGiftLines);
  document.addEventListener('theme:cart:refresh', lockGiftLines);
  document.addEventListener('shopify:section:load', lockGiftLines);

  document.addEventListener('theme:cart:change', unstickImages);
  document.addEventListener('theme:cart:update', unstickImages);
  document.addEventListener('theme:cart:refresh', unstickImages);
  document.addEventListener('theme:cart-drawer:show', unstickImages);
  document.addEventListener('shopify:section:load', unstickImages);

  document.addEventListener('theme:cart:change', closeIfEmptied);
  document.addEventListener('theme:cart:update', closeIfEmptied);
  document.addEventListener('theme:cart:refresh', closeIfEmptied);

  // Cart line images ship as <div class="lazy-image is-loading"><img loading="lazy">
  // and Broadcast's loader is what clears is-loading once each one decodes. That
  // loader never runs in this drawer, so the class stuck, the placeholder held
  // the image at zero size and near-zero opacity, and the browser then never
  // lazy-loaded it because it was never visible. Self-locking: the images were
  // present, correctly sourced, and invisible. Finish the job ourselves.
  function unstickImages() {
    var wraps = document.querySelectorAll('[data-items-holder] .lazy-image, .cb-items .lazy-image');
    for (var i = 0; i < wraps.length; i++) wraps[i].classList.remove('is-loading');
    var imgs = document.querySelectorAll('[data-items-holder] img, .cb-items img');
    for (var j = 0; j < imgs.length; j++) {
      var img = imgs[j];
      img.classList.remove('is-loading');
      if (img.getAttribute('loading') === 'lazy') img.setAttribute('loading', 'eager');
      if (!img.complete) {
        // re-assigning src kicks off a load the lazy path never started
        var src = img.getAttribute('src');
        if (src) img.setAttribute('src', src);
      }
    }
  }

  // The markup has definitely been replaced by the time this fires.
  document.addEventListener('shopify:section:load', syncGiftPicker);

  // Delegated, because the strip sits inside the ladder and is replaced
  // wholesale on every cart refresh.
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    if (!e.target.closest('[data-cb-credit-jump]')) return;
    e.preventDefault();
    jumpToClub();
  });

  syncGiftPicker();
  syncProtection();
  applyClubDismissal();
  unstickImages();
  lockGiftLines();
  hideProtectionLine();
  enforceOneGift();
  dropProtectionIfDigital();
  // seed the latch so a page that loads with items in the bag is not treated
  // as having just been emptied on the first cart event
  fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
    .then(function (r) { return r.json(); })
    .then(function (c) { wasNonEmpty = c.item_count > 0; })
    .catch(function () {});
})();

